import React from 'react'
import {
  Modal,
  Button,
  Alert,
} from 'react-bootstrap'
import moment from 'moment'
import { handleReportRequest } from '../../modules/report'
import { requestReport } from '../../api'
import { createDateRange } from '../../utils/dateUtils'

const recordPerPage = 1000

class ExportDialog extends React.Component {
  state = {
    productSummary: null,
    selectedProduct: null, // null for "All Products", or product object
    pageRanges: null,
    loading: false,
    error: null,
    view: 'list', // 'list' or 'detail'
  }

  componentDidMount() {
    this.fetchProductSummary()
  }

  componentDidUpdate(prevProps, prevState) {
    // Fetch product summary when date or searchArchived changes
    if (
      this.props.dateValue &&
      (!moment(this.props.dateValue).isSame(prevProps.dateValue, 'day') ||
        this.props.searchArchived !== prevProps.searchArchived)
    ) {
      this.fetchProductSummary()
      this.setState({ view: 'list', selectedProduct: null, pageRanges: null })
    }

    // Fetch page ranges when product is selected, date changes, or searchArchived changes
    if (
      this.state.selectedProduct !== null &&
      this.props.dateValue &&
      this.state.view === 'detail' &&
      (this.state.selectedProduct !== prevState.selectedProduct ||
        !moment(this.props.dateValue).isSame(prevProps.dateValue, 'day') ||
        this.props.searchArchived !== prevProps.searchArchived)
    ) {
      this.fetchPageRanges()
    }
  }

  fetchProductSummary = () => {
    this.setState({ loading: true, error: null })

    // Convert dateValue to startDate/endDate using user's timezone
    const dateRange = createDateRange(this.props.dateValue)
    const url = new URL('/api/serviceTxns/product-summary', window.location.origin)
    url.searchParams.set('startDate', dateRange.start)
    url.searchParams.set('endDate', dateRange.end)

    if (this.props.searchArchived) {
      url.searchParams.set('source', 'archived')
    }

    fetch(url.href, {
      headers: {
        Authorization: `Bearer ${this.props.jwt}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load product summary')
        }
        return response.json()
      })
      .then((data) => {
        this.setState({ productSummary: data, loading: false })
      })
      .catch((err) => {
        this.setState({
          error: 'Unable to load product summary. Please try again.',
          loading: false,
        })
      })
  }

  fetchPageRanges = () => {
    this.setState({ loading: true, error: null })

    // Convert dateValue to startDate/endDate using user's timezone
    const dateRange = createDateRange(this.props.dateValue)
    const url = new URL('/api/serviceTxns/page-ranges', window.location.origin)
    url.searchParams.set('startDate', dateRange.start)
    url.searchParams.set('endDate', dateRange.end)
    url.searchParams.set('pageSize', recordPerPage)

    // Add productId only if a specific product is selected (not "All Products")
    if (this.state.selectedProduct && this.state.selectedProduct.productId) {
      url.searchParams.set('productId', this.state.selectedProduct.productId)
    }

    if (this.props.searchArchived) {
      url.searchParams.set('source', 'archived')
    }

    fetch(url.href, {
      headers: {
        Authorization: `Bearer ${this.props.jwt}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load page ranges')
        }
        return response.json()
      })
      .then((data) => {
        this.setState({ pageRanges: data, loading: false })
      })
      .catch((err) => {
        this.setState({
          error: 'Unable to load page information. Please try again.',
          loading: false,
        })
      })
  }

  handleProductSelect = (product) => {
    this.setState({
      selectedProduct: product,
      view: 'detail',
      pageRanges: null,
      error: null,
    })
  }

  handleBackToList = () => {
    this.setState({
      view: 'list',
      selectedProduct: null,
      pageRanges: null,
    })
  }

  handleDownloadAllCSV = () => {
    if (!this.props.dateValue) return

    // Convert dateValue to startDate/endDate using user's timezone
    const dateRange = createDateRange(this.props.dateValue)

    const params = {
      type: 'csv',
      startDate: dateRange.start,
      endDate: dateRange.end,
    }

    // Add productId only if a specific product is selected (not "All Products")
    if (this.state.selectedProduct && this.state.selectedProduct.productId) {
      params.productId = this.state.selectedProduct.productId
    }

    if (this.props.searchArchived) {
      params.source = 'archived'
    }

    handleReportRequest(
      requestReport('/api/reports/queue/serviceTxnReport', params),
    )
  }

  handleViewPage = (page) => {
    if (!this.props.dateValue || !this.state.pageRanges) return

    // Convert dateValue to startDate/endDate using user's timezone
    const dateRange = createDateRange(this.props.dateValue)

    const url = new URL('/report/web', window.location.href)
    url.searchParams.set('name', 'serviceTxnReport')
    url.searchParams.set('startDate', dateRange.start)
    url.searchParams.set('endDate', dateRange.end)
    url.searchParams.set('fromSeq', page.fromSeq)
    url.searchParams.set('toSeq', page.toSeq)

    // Add product info only if a specific product is selected (not "All Products")
    if (this.state.selectedProduct && this.state.selectedProduct.productId) {
      url.searchParams.set('productId', this.state.selectedProduct.productId)
      url.searchParams.set('productName', this.state.selectedProduct.productName)
    }

    if (this.props.searchArchived) {
      url.searchParams.set('source', 'archived')
    }

    url.searchParams.set('pageNumber', page.pageNumber)
    url.searchParams.set('totalPages', this.state.pageRanges.pageCount)
    window.open(url.href, '_blank')
  }

  renderProductList() {
    const { productSummary, loading } = this.state

    if (loading) {
      return (
        <div className="text-center" style={{ padding: 40 }}>
          <i className="glyphicon glyphicon-refresh spinning"></i> Loading products...
        </div>
      )
    }

    if (!productSummary || !productSummary.products) {
      return null
    }

    return (
      <div>
        <div className="mb-4">
          <strong>Select a product to export:</strong>
        </div>

        <div className="mb-5">
          <Button
            bsStyle="info"
            bsSize="large"
            block
            onClick={() => this.handleProductSelect({ productName: 'All Products' })}
          >
            <strong>All Products</strong>
            <div className="text-sm mt-1">
              {productSummary.totalRecords.toLocaleString()} total records
            </div>
          </Button>
        </div>

        {productSummary.products.length > 0 ? (
          <div className="max-h-[400px] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2">
              {productSummary.products.map((product) => (
                <Button
                  key={product.productId}
                  bsStyle="default"
                  onClick={() => this.handleProductSelect(product)}
                  className="text-left p-2.5 whitespace-normal"
                >
                  <div className="font-bold break-words">{product.productName}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    {product.transactionCount.toLocaleString()} records ({product.percentage}%)
                  </div>
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center p-5 text-gray-400">
            No products found for this date
          </div>
        )}
      </div>
    )
  }

  renderDetailView() {
    const { selectedProduct, pageRanges, loading } = this.state

    return (
      <div>
        <div className="mb-5">
          <Button onClick={this.handleBackToList} bsStyle="link" className="p-0">
            <span className="glyphicon glyphicon-arrow-left"></span> Back to product list
          </Button>
        </div>

        <div className="mb-5">
          <h4 className="m-0">
            {selectedProduct.productName}
          </h4>
          {selectedProduct.productId && (
            <div className="text-sm text-gray-600 mt-1">
              {selectedProduct.transactionCount.toLocaleString()} records
            </div>
          )}
        </div>

        {loading && (
          <div className="text-center p-10">
            <i className="glyphicon glyphicon-refresh spinning"></i> Loading page information...
          </div>
        )}

        {pageRanges && !loading && (
          <div>
            <div className="mt-5 mb-4">
              <strong>
                {pageRanges.totalRecords > 0
                  ? `${pageRanges.totalRecords.toLocaleString()} records found`
                  : 'No service transactions found'}
              </strong>
            </div>

            {pageRanges.totalRecords > 0 && (
              <div>
                <div className="mb-4">
                  <div className="border-t border-gray-300 mb-2.5 pt-2.5">
                    <strong>Download Options</strong>
                  </div>
                  <Button
                    bsStyle="success"
                    onClick={this.handleDownloadAllCSV}
                    block
                  >
                    <span className="glyphicon glyphicon-download-alt"></span>{' '}
                    Download All CSV
                  </Button>
                </div>

                <div>
                  <div className="border-t border-gray-300 mb-2.5 pt-2.5">
                    <strong>View for Printing</strong>
                    <div className="text-sm text-gray-600">
                      ({recordPerPage} records per page)
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {pageRanges.pages.map((page) => (
                      <Button
                        key={page.pageNumber}
                        bsStyle="primary"
                        onClick={() => this.handleViewPage(page)}
                      >
                        <span className="glyphicon glyphicon-file"></span> Page{' '}
                        {page.pageNumber}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  render() {
    const { show, onHide } = this.props
    const { error, view } = this.state

    return (
      <Modal show={show} onHide={onHide} bsSize="large">
        <Modal.Header closeButton>
          <Modal.Title>Export Service Transactions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert bsStyle="danger">{error}</Alert>}

          {view === 'list' ? this.renderProductList() : this.renderDetailView()}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default ExportDialog
